import { cellAtWorld, cellCenter, flowNext, geo, heroWalkable, touchGrid } from '../grid.ts';
import { hexLen } from '../hex.ts';
import { dist2, imax, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero, WedgeRules, WedgeState } from '../types.ts';
import { NIGHTMARES, type NightmareType } from '@somnhold/content';
import { ev } from './combat.ts';
import { attackHero } from './enemies.ts';
import { orderMove } from './heroes.ts';

/**
 * The wedges, small (docs/sendas/guerrero.md §4, «Las cuñas»; the ground from battle/wedge-layout.ts). The yard wall
 * round the guard house has six gaps; the hero carries three shields. Standing still by a gap a moment plants one in it
 * (the gap shuts: the column's way is worked out again at once, and the board draws it on the ground) or pulls it out
 * again. The column takes the shortest way to the house; where its way is one hex wide it goes in file at half pace,
 * so it bunches there. Under the cornice is the strip, with its counter; standing still a moment in the nook by the
 * strip pushes the loose rock, which falls a moment later on whatever is in the strip. In through the door too many,
 * the act is lost; the column gone without that, won.
 *
 * Cost: the one-hex-wide cells are worked out again only when the grid changes (a shield in or out), by counting each
 * cell's open neighbours; the column is the fight's only nightmares, looked at once a tick each.
 */

const fresh = (r: WedgeRules): WedgeState => ({
  shut: r.gaps.map(() => 0),
  hand: r.shields,
  at: -1,
  held: 0,
  pushed: -1,
  down: false,
  count: 0,
  entered: 0,
  narrow: [],
  rev: -1,
});

/** The gaps' shields as the grid's plugs (sorted cell ids). */
const plugsOf = (r: WedgeRules, w: WedgeState): number[] =>
  r.gaps.filter((_, i) => w.shut[i] === 1).sort((a, b) => a - b);

/** Steps from `from` over the open cells (−1 unreachable). */
const bfs = (nb: Int32Array, open: Uint8Array, from: number): Int16Array => {
  const d = new Int16Array(open.length).fill(-1);
  if (from < 0 || !open[from]) return d;
  const q = new Int16Array(open.length);
  let head = 0;
  let tail = 0;
  d[from] = 0;
  q[tail++] = from;
  while (head < tail) {
    const c = q[head++] as number;
    for (let k = c * 6; k < c * 6 + 6; k++) {
      const x = nb[k] as number;
      if (x < 0 || !open[x] || (d[x] as number) >= 0) continue;
      d[x] = (d[c] as number) + 1;
      q[tail++] = x;
    }
  }
  return d;
};

/**
 * The cells where the column's way is one hex wide: every shortest way from where it comes in to the house goes through
 * them (the only cell at its distance), and the way neither comes in nor goes on wider beside them (one cell before and
 * one after, on any shortest way), with rock or a shield on either hand (three open neighbours at most): a ledge
 * between the cliff and the wall. Open ground isn't, nor a gap that opens onto the yard, nor the rim where it comes in.
 */
const narrowCells = (s: BattleState, r: WedgeRules): number[] => {
  const R = s.map.radius;
  const g = geo(R);
  const nb = g.nb;
  const n = (2 * R + 1) * (2 * R + 1);
  const open = new Uint8Array(n);
  for (let id = 0; id < n; id++) open[id] = heroWalkable(s, id) ? 1 : 0;
  const house = cellAtWorld(s, 0, 0);
  const da = bfs(nb, open, r.entry);
  const db = bfs(nb, open, house);
  const len = house >= 0 ? (da[house] as number) : -1;
  if (len < 0) return [];
  const on = (id: number): boolean => (da[id] as number) >= 0 && (db[id] as number) >= 0 && (da[id] as number) + (db[id] as number) === len;
  const count = new Int16Array(len + 1);
  const last = new Int32Array(len + 1);
  for (let id = 0; id < n; id++) {
    if (!on(id)) continue;
    const x = da[id] as number;
    count[x] = (count[x] as number) + 1;
    last[x] = id;
  }
  const out: number[] = [];
  for (let l = 1; l < len; l++) {
    if (count[l] !== 1 || count[l - 1] !== 1 || count[l + 1] !== 1) continue;
    const id = last[l] as number;
    if (hexLen(g.q[id] as number, g.r[id] as number) >= s.map.bastion) continue;
    // Hemmed in by rock or a shield (a straight run over open ground is one shortest way too, and wide).
    let deg = 0;
    for (let k = id * 6; k < id * 6 + 6; k++) {
      const x = nb[k] as number;
      if (x >= 0 && open[x]) deg++;
    }
    if (deg <= 3) out.push(id);
  }
  return out.sort((a, b) => a - b);
};

/** The column's way from where it comes in to the house, cell by cell (as the nightmares walk it now). */
export const wedgeRoute = (s: BattleState): number[] => {
  const r = s.rules?.senda?.wedge;
  if (!r) return [];
  const out: number[] = [r.entry];
  let at = r.entry;
  for (let i = 0; i < 80; i++) {
    const next = flowNext(s, at);
    if (next < 0 || next === at) break;
    out.push(next);
    at = next;
  }
  return out;
};

const centre = (s: BattleState, id: number): { x: number; y: number } => cellCenter(s.map.radius, id);

/** Where the hero goes to work a gap (its middle) or the rock (the nook). */
const spotOf = (s: BattleState, r: WedgeRules, at: number): { x: number; y: number } => centre(s, at < r.gaps.length ? (r.gaps[at] as number) : r.nook);

/**
 * The hero sent to a gap or the rock (a tap on it, docs/SENDAS.md: «se toca una cosa; el héroe va y lo hace»): it walks
 * there and, standing still by it, works it. Any other order calls it off (battle/heroes.ts).
 */
export const wedgeOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.wedge;
  const w = s.senda?.wedge;
  if (!r || !w || at < 0 || at > r.gaps.length || (at === r.gaps.length && w.pushed >= 0)) return;
  w.at = at;
  w.held = 0;
  const p = spotOf(s, r, at);
  orderMove(s, h, p.x, p.y);
  h.target = 0;
  h.forced = false;
};

/** A shield in: the gap shuts (whoever stands in it steps back to the house's side). */
const plant = (s: BattleState, r: WedgeRules, w: WedgeState, i: number): void => {
  const g = r.gaps[i] as number;
  w.shut[i] = 1;
  w.hand--;
  s.plugs = plugsOf(r, w);
  touchGrid(s);
  const c = centre(s, g);
  for (const h of s.heroes) {
    if (cellAtWorld(s, h.x, h.y) !== g) continue;
    // Onto the open neighbour nearest the house.
    let to = -1;
    let td = 0x7fffffff;
    for (let k = g * 6; k < g * 6 + 6; k++) {
      const x = geo(s.map.radius).nb[k] as number;
      if (x < 0 || !heroWalkable(s, x)) continue;
      const p = centre(s, x);
      const d = dist2(p.x, p.y, 0, 0);
      if (d < td) {
        td = d;
        to = x;
      }
    }
    if (to >= 0) {
      const p = centre(s, to);
      h.x = h.destX = h.ax = p.x;
      h.y = h.destY = h.ay = p.y;
      h.path = [];
      h.moving = false;
    }
  }
  ev(s, { t: 'senda', kind: 'plant', npc: '', x: c.x, y: c.y, n: i });
};

const pull = (s: BattleState, r: WedgeRules, w: WedgeState, i: number): void => {
  const c = centre(s, r.gaps[i] as number);
  w.shut[i] = 0;
  w.hand++;
  s.plugs = plugsOf(r, w);
  touchGrid(s);
  ev(s, { t: 'senda', kind: 'pull', npc: '', x: c.x, y: c.y, n: i });
};

/**
 * The wedges' hands, both sizes (the ledge's shields, chapter 1; the avalanche's iron wedges, chapter 4, battle/slide.ts):
 * the hero sent to a spot works it by standing still within `reach` of it. `standBy`: false (and the count starts over)
 * while it walks or stands off; `standDone`: one more tick still, true once `stand` of them (the job then done).
 */
export const standBy = (h: Hero, job: { held: number }, x: number, y: number, reach: number): boolean => {
  if (h.moving || !withinRange(h.x, h.y, x, y, reach)) {
    job.held = 0;
    return false;
  }
  return true;
};

export const standDone = (job: { at: number; held: number }, stand: number): boolean => {
  if (++job.held < stand) return false;
  job.held = 0;
  job.at = -1;
  return true;
};

/** The hero's hands: sent to a gap or the rock and standing still by it, the shield goes in or out, or the rock is pushed. */
const heroWork = (s: BattleState, r: WedgeRules, w: WedgeState, cue: (c: string) => void): void => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!h || w.at < 0) return;
  const at = w.at;
  const p = spotOf(s, r, at);
  if (!standBy(h, w, p.x, p.y, r.reach)) return;
  // A gap: in if there's a shield in hand and nothing stands in it; out if one is in. The rock: once.
  const gap = at < r.gaps.length;
  const can = gap ? w.shut[at] === 1 || (w.hand > 0 && !s.enemies.some((e) => e.hp > 0 && cellAtWorld(s, e.x, e.y) === r.gaps[at])) : w.pushed < 0;
  if (!can) {
    w.held = 0;
    if (gap && w.shut[at] !== 1 && w.hand <= 0) w.at = -1;
    return;
  }
  if (!standDone(w, r.stand)) return;
  if (!gap) {
    w.pushed = s.tick;
    ev(s, { t: 'senda', kind: 'push', npc: '', x: p.x, y: p.y });
    cue('push');
  } else if (w.shut[at] === 1) pull(s, r, w, at);
  else {
    const first = w.shut.every((x) => x === 0) && w.hand === r.shields;
    plant(s, r, w, at);
    if (first) cue('plant');
  }
};

/** Out of the column: in through the door (no kill, no death: it got in). */
const inside = (s: BattleState, r: WedgeRules, w: WedgeState): void => {
  let gone = false;
  for (const e of s.enemies) {
    if (e.hp <= 0 || !withinRange(e.x, e.y, 0, 0, r.door)) continue;
    gone = true;
    w.entered++;
    ev(s, { t: 'senda', kind: 'enter', npc: '', x: e.x, y: e.y, n: w.entered });
  }
  if (gone) s.enemies = s.enemies.filter((e) => e.hp <= 0 || !withinRange(e.x, e.y, 0, 0, r.door));
};

export const wedgeTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.wedge;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.wedge ??= fresh(r));
  if (w.rev !== s.gridRev) {
    w.narrow = narrowCells(s, r);
    w.rev = s.gridRev;
  }
  if (!s.started) return;
  heroWork(s, r, w, cue);
  // The rock: a moment after the push, down on the strip, on whatever is in it.
  if (w.pushed >= 0 && !w.down && s.tick >= w.pushed + r.fall) {
    w.down = true;
    const pid = s.heroes[0]?.pid;
    let n = 0;
    for (const e of s.enemies) {
      if (e.hp <= 0 || !r.strip.includes(cellAtWorld(s, e.x, e.y))) continue;
      e.hp = 0;
      if (pid) e.lastPid = pid;
      n++;
    }
    const m = centre(s, r.strip[1] as number);
    ev(s, { t: 'senda', kind: 'fall', npc: '', x: m.x, y: m.y, n });
    ev(s, { t: 'boom', x: m.x, y: m.y, r: 1500, kind: 'cornice' });
    cue('fall');
  }
  // The column: in file and at half pace where its way is one hex wide; the strip's count. It doesn't stop for the hero
  // (it is going for the house), but whoever passes in reach strikes as it goes.
  let count = 0;
  const hero = s.heroes.find((h) => h.downTicks === 0);
  for (const e of s.enemies) {
    if (e.hp <= 0) continue;
    const c = cellAtWorld(s, e.x, e.y);
    if (r.strip.includes(c)) count++;
    if (binHas(w.narrow, c)) slow(e, r.slowBp);
    if (hero && e.cd === 0 && e.taunter === 0 && withinRange(e.x, e.y, hero.x, hero.y, NIGHTMARES[e.kind as NightmareType].reach + 150)) attackHero(s, e, hero);
  }
  w.count = count;
  inside(s, r, w);
};

const binHas = (a: number[], v: number): boolean => {
  let lo = 0;
  let hi = a.length - 1;
  while (lo <= hi) {
    const m = (lo + hi) >> 1;
    const x = a[m] as number;
    if (x === v) return true;
    if (x < v) lo = m + 1;
    else hi = m - 1;
  }
  return false;
};

/** Held to the file's pace for the next tick (a stronger slow already on it stays). */
const slow = (e: Enemy, bp: number): void => {
  if (e.slowTicks < 2) e.slowTicks = 2;
  e.slowBp = imax(e.slowBp, bp);
};

/** Lost with too many in through the door; won with the whole column out and dealt with. */
export const wedgeResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.senda?.wedge;
  const w = s.senda?.wedge;
  if (!r || !w) return null;
  if (w.entered >= r.lose) return 'lost';
  if (s.wave.next >= s.wave.spawns.length && !s.enemies.some((e) => e.hp > 0)) return 'won';
  return null;
};

/** How far along: the column dealt with (fallen or in), of its whole. */
export const wedgeGoal = (s: BattleState): { done: number; need: number } => {
  const total = s.wave.spawns.length;
  const alive = s.enemies.filter((e) => e.hp > 0).length;
  return { done: s.wave.next - alive, need: total > 0 ? total : 1 };
};
